import { FC, useCallback, useMemo, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useStore } from '@hooks/useStore';
import { useMyTranslations } from '@hooks/useMyTranslations';
import { NavbarButton } from '@components/NavBarComponents/NavbarButton/NavbarButton';
import { CountryCodeSelect } from '@components/Core/CountryCodeSelect';
import LogoIcon from '@icons/logo_icon.svg?component';
import LogoutIcon from '@icons/navbar/navbar_logout.svg?component';
import ProfileIcon from '@icons/navbar/navbar_profile.svg?component';
import NavMenuIcon from '@icons/navbar/navbar_menu.svg?component';
import CloseIcon from '@icons/navbar/navbar_close.svg?component';
import ChevronIcon from '@icons/inputs/chevron_icon.svg?component';
// import Package from '../../../../package.json';
import { NavBarStatusType, NavbarTransFields } from './types';
import { navbarTranslations } from './translations';
import { iconProps } from './constatnts';

export const Navbar: FC = observer(() => {
  const {
    profileStore: { usersNavBar, user },
    // mainStore: { theme },
    authStore: { logout },
  } = useStore();

  const [navStatus, setNavStatus] = useState<NavBarStatusType>('close');

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { t } = useMyTranslations<NavbarTransFields>([navbarTranslations.name]);

  const onNavItemClick = useCallback((path: string) => {
    navigate(path);
    setNavStatus('close');
  }, []);

  const navStyle = useMemo(() => {
    const style =
      'relative w-full h-full z-2 shadow-[0_0_6px_rgba(70,70,70,0.15)] sm-nav-bar transition-[max-width] duration-200 ease-out bg-grey';
    switch (navStatus) {
      case 'close':
        return `${style} max-w-[64px]`;
      case 'open':
        return `${style} max-w-[250px]`;
      case 'full':
        return `${style} !max-w-[100vw]`;
    }
  }, [navStatus]);

  return (
    <>
      {/* TOP NAV BAR FOR MOBILE */}
      <div className="absolute w-[100vw] h-[108px] z-10 border-b border-container-bg md-nav-bar-top-hide bg-[#f6f8ff]">
        <div className="pt-[60px] pb-[16px] px-[20px] flex items-center justify-between ">
          <Link to="/" className={`h-[32px] flex items-center gap-x-[8px]`}>
            <LogoIcon width={32} height={32} />
            <p className="text-primary">GeoBuyer</p>
          </Link>
          <div className="flex-centred gap-x-[12px]">
            <button
              type="button"
              onClick={() => navigate('/profile')}
              className=" h-[32px] w-[32px]"
            >
              <ProfileIcon width={32} height={32} />
            </button>
            <button type="button" onClick={() => setNavStatus('full')}>
              <NavMenuIcon width={32} height={32} />
            </button>
          </div>
        </div>
      </div>
      {/* DEFAULT LEFT SIDE NAV BAR */}
      <div className={navStyle}>
        <div
          className={`relative z-20 h-screen w-full py-[20px] px-[16px] flex flex-col justify-between items-start gap-y-[18px] box-border bg-white sm-nav-bar-content `}
        >
          <div className="flex items-center justify-between w-full">
            <Link to="/" className={`h-[32px] flex items-center gap-x-[8px]`}>
              <LogoIcon width={32} height={32} />
              {navStatus !== 'close' && <p className="text-primary">GeoBuyer</p>}
            </Link>
            {navStatus === 'full' && (
              <button type="button" onClick={() => setNavStatus('close')}>
                <CloseIcon width={24} height={24} />
              </button>
            )}
          </div>
          {navStatus === 'full' && <CountryCodeSelect stylesProp="relative w-full !block left-0" />}
          <nav className="flex flex-col items-start px-[4px] mt-5 mb-auto w-full">
            {usersNavBar.map((item, i) => (
              <div
                key={item.label + i}
                className={`${navStatus === 'full' && 'border-b w-full'} pb-[12px]`}
              >
                <NavbarButton
                  icon={item.icon}
                  iconWidth={24}
                  text={t(item.label)}
                  onClick={() => onNavItemClick(item.link.path)}
                  disabled={item.disable}
                  opacity={pathname.startsWith(item.link.path) ? 100 : undefined}
                  navOpen={navStatus !== 'close'}
                />
              </div>
            ))}
          </nav>
          <div className="flex flex-col items-start justify-center gap-y-[18px]">
            <NavbarButton
              icon={<ProfileIcon {...iconProps} width={32} height={32} />}
              // text={t('profile')}
              text={user?.name || t('profile')}
              onClick={() => navigate('/profile')}
              opacity={100}
              navOpen={navStatus !== 'close'}
            />
            <NavbarButton
              icon={<LogoutIcon {...iconProps} width={32} height={32} />}
              text={t('logout')}
              onClick={logout}
              opacity={100}
              navOpen={navStatus !== 'close'}
            />
          </div>
          {/* <span className="text-xs">{theme}</span>
          <span className="text-xs">{Package.version}</span> */}
          <button
            type="button"
            onClick={() => setNavStatus((prev) => (prev === 'close' ? 'open' : 'close'))}
            className="hide-on-mobile"
          >
            <ChevronIcon
              width={32}
              height={32}
              className={navStatus !== 'close' ? 'rotate-90' : '-rotate-90'}
            />
            <span className="text-xs">{import.meta.env.VITE_APP_VERSION || '0.0.1'}</span>
          </button>
        </div>
      </div>
    </>
  );
});
